import { useMemo } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import {
  useListHazardIdentifications,
  useListRiskReductionMeasures,
  useMachineryDirectiveEhsrClauseList,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { useMachineryDirectiveEhsrList } from '../../hooks/useMachineryDirectiveEhsrList'
import { EhsrOverviewNodeCard } from './EhsrOverviewNodeCard'
import { buildEhsrOverviewModel } from './buildEhsrOverviewModel'

export function EhsrsOverviewPage() {
  const params = useParams<{ machineryDirectiveComplianceId: string }>()
  const complianceId = params.machineryDirectiveComplianceId!
  const { t } = useTranslation('machineryDirectiveCompliance')

  const ehsrClauses = useMachineryDirectiveEhsrList()
  const {
    isLoading: isLoadingEhsrs,
    isError: isEhsrsError,
    error: ehsrsError,
    refetch: refetchEhsrs,
  } = useMachineryDirectiveEhsrClauseList()
  const {
    data: hazards = [],
    isLoading: isLoadingHazards,
    isError: isHazardsError,
    error: hazardsError,
    refetch: refetchHazards,
  } = useListHazardIdentifications(complianceId)
  const {
    data: measures = [],
    isLoading: isLoadingMeasures,
    isError: isMeasuresError,
    error: measuresError,
    refetch: refetchMeasures,
  } = useListRiskReductionMeasures(complianceId)

  const isLoading = isLoadingHazards || isLoadingMeasures || isLoadingEhsrs
  const isError = isHazardsError || isMeasuresError || isEhsrsError
  const errorMessage =
    hazardsError?.message ?? measuresError?.message ?? ehsrsError?.message

  const overviewNodes = useMemo(
    () =>
      buildEhsrOverviewModel({
        clauses: ehsrClauses,
        hazards,
        measures,
      }),
    [ehsrClauses, hazards, measures]
  )

  const handleRetry = () => {
    void refetchHazards()
    void refetchMeasures()
    void refetchEhsrs()
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <ComplianceSubPageHeader>
        <div className='space-y-1'>
          <ComplianceSubPageTitle>
            {t('ehsrsOverviewPage.title')}
          </ComplianceSubPageTitle>
          <p className='text-sm text-muted-foreground'>
            {t('ehsrsOverviewPage.description')}
          </p>
        </div>
      </ComplianceSubPageHeader>

      {isError && (
        <Alert variant='destructive' className='mb-4'>
          <AlertTriangle className='size-4' />
          <AlertTitle>{t('ehsrsOverviewPage.loadFailedTitle')}</AlertTitle>
          <AlertDescription className='flex flex-wrap items-center gap-3'>
            <span>{errorMessage ?? t('ehsrsOverviewPage.loadFailed')}</span>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={handleRetry}
            >
              {t('ehsrsOverviewPage.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className='space-y-3'>
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className='h-20 w-full rounded-lg' />
          ))}
        </div>
      )}

      {!isLoading && !isError && (
        <div className='space-y-2'>
          {overviewNodes.map((node) => (
            <EhsrOverviewNodeCard key={node.nodeId} node={node} />
          ))}
        </div>
      )}
    </div>
  )
}
